import type { AppointmentStatus, MemberStatus, PipelineStage } from './db/types';

export interface ReportingMember {
  id: string;
  status?: MemberStatus | string;
  contact?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  createdAt?: unknown;
  joinedAt?: unknown;
}

export interface ReportingLead {
  id: string;
  pipelineStage?: PipelineStage | string;
  source?: string | null;
  createdAt?: unknown;
}

export interface ReportingCheckIn {
  id: string;
  timestamp?: unknown;
  createdAt?: unknown;
  method?: string;
}

export interface ReportingInvoice {
  id: string;
  status?: string;
  total?: number;
  amountDue?: number;
  amountPaid?: number;
  currency?: string;
  dueDate?: unknown;
  paidAt?: unknown;
  createdAt?: unknown;
}

export interface ReportingAppointment {
  id: string;
  status?: AppointmentStatus | string;
  startTime?: unknown;
  serviceId?: string;
  providerId?: string;
}

export interface ReportingData {
  members: ReportingMember[];
  leads: ReportingLead[];
  checkIns: ReportingCheckIn[];
  invoices: ReportingInvoice[];
  appointments: ReportingAppointment[];
}

export interface ReportingRange {
  start: Date;
  end: Date;
}

export interface MetricCard {
  label: string;
  value: string;
  helper?: string;
}

export interface BreakdownItem {
  label: string;
  value: number;
}

export interface ReportingSummary {
  activeMembers: number;
  pausedMembers: number;
  canceledMembers: number;
  newMembers: number;
  newLeads: number;
  wonLeads: number;
  leadConversionRate: number;
  attendance: number;
  attendancePreviousPeriod: number;
  appointments: number;
  completedAppointments: number;
  noShowAppointments: number;
  canceledAppointments: number;
  revenueCollected: number;
  scheduledRevenue: number;
  overdueBalance: number;
  overdueInvoices: ReportingInvoice[];
  memberStatusBreakdown: BreakdownItem[];
  leadStageBreakdown: BreakdownItem[];
  appointmentStatusBreakdown: BreakdownItem[];
  attendanceByDay: BreakdownItem[];
  revenueByDay: BreakdownItem[];
  topLeadSources: BreakdownItem[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === 'object') {
    const candidate = value as {
      toDate?: () => Date;
      seconds?: number;
      _seconds?: number;
    };
    if (typeof candidate.toDate === 'function') return candidate.toDate();
    if (typeof candidate.seconds === 'number') return new Date(candidate.seconds * 1000);
    if (typeof candidate._seconds === 'number') return new Date(candidate._seconds * 1000);
  }
  return null;
}

export function startOfDay(date: Date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

export function endOfDay(date: Date) {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
}

export function defaultReportRange(): ReportingRange {
  const now = new Date();
  return {
    start: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)),
    end: endOfDay(now),
  };
}

export function previousRange(range: ReportingRange): ReportingRange {
  const span = Math.max(DAY_MS, range.end.getTime() - range.start.getTime());
  return {
    start: new Date(range.start.getTime() - span),
    end: new Date(range.end.getTime() - span),
  };
}

export function formatDateInput(date: Date) {
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

export function formatCurrency(cents: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export function formatPercent(value: number) {
  return `${Math.round(value)}%`;
}

export function dateLabel(date: Date) {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function inRange(value: unknown, range: ReportingRange) {
  const date = toDate(value);
  if (!date) return false;
  return date.getTime() >= range.start.getTime() && date.getTime() <= range.end.getTime();
}

function dayKey(date: Date) {
  return formatDateInput(date);
}

function daySeries(range: ReportingRange) {
  const days: BreakdownItem[] = [];
  const cursor = startOfDay(range.start);
  const end = endOfDay(range.end);

  while (cursor.getTime() <= end.getTime()) {
    days.push({ label: dayKey(cursor), value: 0 });
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
}

function invoiceAmount(invoice: ReportingInvoice, paidOnly = false) {
  if (paidOnly) {
    return invoice.amountPaid ?? invoice.total ?? 0;
  }
  return invoice.amountDue ?? invoice.total ?? 0;
}

function incrementBreakdown(items: Map<string, number>, label: string, amount = 1) {
  items.set(label, (items.get(label) || 0) + amount);
}

function toBreakdown(items: Map<string, number>) {
  return [...items.entries()]
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
}

export function computeReportingSummary(data: ReportingData, range: ReportingRange): ReportingSummary {
  const priorRange = previousRange(range);
  const activeMembers = data.members.filter((member) => member.status === 'active').length;
  const pausedMembers = data.members.filter((member) => member.status === 'paused').length;
  const canceledMembers = data.members.filter((member) => member.status === 'canceled').length;
  const newMembers = data.members.filter((member) => inRange(member.joinedAt || member.createdAt, range)).length;
  const rangeLeads = data.leads.filter((lead) => inRange(lead.createdAt, range));
  const wonLeads = rangeLeads.filter((lead) => lead.pipelineStage === 'won').length;
  const attendance = data.checkIns.filter((checkIn) => inRange(checkIn.timestamp || checkIn.createdAt, range)).length;
  const attendancePreviousPeriod = data.checkIns.filter((checkIn) =>
    inRange(checkIn.timestamp || checkIn.createdAt, priorRange)
  ).length;
  const rangeAppointments = data.appointments.filter((appointment) => inRange(appointment.startTime, range));

  const paidInvoices = data.invoices.filter((invoice) => {
    if (invoice.status !== 'paid') return false;
    return inRange(invoice.paidAt || invoice.createdAt, range);
  });
  const revenueCollected = paidInvoices.reduce((total, invoice) => total + invoiceAmount(invoice, true), 0);

  const scheduledRevenue = data.invoices
    .filter((invoice) => invoice.status === 'open' || invoice.status === 'draft')
    .filter((invoice) => inRange(invoice.dueDate || invoice.createdAt, range))
    .reduce((total, invoice) => total + invoiceAmount(invoice), 0);

  const now = new Date();
  const overdueInvoices = data.invoices.filter((invoice) => {
    const dueDate = toDate(invoice.dueDate);
    return !!dueDate && dueDate.getTime() < now.getTime() && ['open', 'uncollectible'].includes(invoice.status || '');
  });
  const overdueBalance = overdueInvoices.reduce((total, invoice) => total + invoiceAmount(invoice), 0);

  const memberStatuses = new Map<string, number>();
  data.members.forEach((member) => incrementBreakdown(memberStatuses, member.status || 'unknown'));

  const leadStages = new Map<string, number>();
  rangeLeads.forEach((lead) => incrementBreakdown(leadStages, lead.pipelineStage || 'unknown'));

  const appointmentStatuses = new Map<string, number>();
  rangeAppointments.forEach((appointment) => incrementBreakdown(appointmentStatuses, appointment.status || 'unknown'));

  const sourceBreakdown = new Map<string, number>();
  rangeLeads.forEach((lead) => incrementBreakdown(sourceBreakdown, lead.source || 'Unknown'));

  const attendanceByDay = daySeries(range);
  const attendanceByDayMap = new Map(attendanceByDay.map((item) => [item.label, item]));
  data.checkIns.forEach((checkIn) => {
    const date = toDate(checkIn.timestamp || checkIn.createdAt);
    if (!date || !inRange(date, range)) return;
    const item = attendanceByDayMap.get(dayKey(date));
    if (item) item.value += 1;
  });

  const revenueByDay = daySeries(range);
  const revenueByDayMap = new Map(revenueByDay.map((item) => [item.label, item]));
  paidInvoices.forEach((invoice) => {
    const date = toDate(invoice.paidAt || invoice.createdAt);
    if (!date) return;
    const item = revenueByDayMap.get(dayKey(date));
    if (item) item.value += invoiceAmount(invoice, true);
  });

  return {
    activeMembers,
    pausedMembers,
    canceledMembers,
    newMembers,
    newLeads: rangeLeads.length,
    wonLeads,
    leadConversionRate: rangeLeads.length ? (wonLeads / rangeLeads.length) * 100 : 0,
    attendance,
    attendancePreviousPeriod,
    appointments: rangeAppointments.length,
    completedAppointments: rangeAppointments.filter((appointment) => appointment.status === 'completed').length,
    noShowAppointments: rangeAppointments.filter((appointment) => appointment.status === 'no_show').length,
    canceledAppointments: rangeAppointments.filter((appointment) => appointment.status === 'canceled').length,
    revenueCollected,
    scheduledRevenue,
    overdueBalance,
    overdueInvoices,
    memberStatusBreakdown: toBreakdown(memberStatuses),
    leadStageBreakdown: toBreakdown(leadStages),
    appointmentStatusBreakdown: toBreakdown(appointmentStatuses),
    attendanceByDay,
    revenueByDay,
    topLeadSources: toBreakdown(sourceBreakdown).slice(0, 6),
  };
}

